import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Plus, Trash2, Edit2, X, LogOut } from 'lucide-react';
import Logo from '@/components/Logo';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AXIOS_CFG = { withCredentials: true };

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const startLogin = () => {
  const redirectUrl = window.location.origin + '/admin';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

const emptyProject = {
  name: '', description: '', location: '', status: 'completed', category: 'commercial',
  images: [''], name_en: '', name_de: '', description_en: '', description_de: '',
};

const emptyNews = { title: '', body: '', image: '', title_en: '', title_de: '', body_en: '', body_de: '' };

const AdminLogin = () => {
  const { t } = useLanguage();
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md text-center">
        <Logo variant="stacked" className="mx-auto mb-12" />
        <div className="text-xs uppercase tracking-[0.32em] text-gold mb-4">— Admin</div>
        <h1 className="font-serif text-white text-3xl tracking-tighter mb-10">{t('admin.title')}</h1>
        <button
          onClick={startLogin}
          data-testid="admin-login-btn"
          className="w-full inline-flex items-center justify-center gap-3 bg-gold text-black px-8 py-4 text-xs uppercase tracking-[0.2em] font-bold hover:bg-[#E6C547] transition-colors"
        >
          {t('admin.login')}
        </button>
      </div>
    </div>
  );
};

const ProjectForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="border border-gold/40 p-8 mb-8 bg-[#141414] space-y-4" data-testid="project-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input placeholder="Name (LT)" value={form.name} onChange={upd('name')} data-testid="pf-name" />
        <Input placeholder="Location" value={form.location} onChange={upd('location')} data-testid="pf-location" />
        <Input placeholder="Name (EN)" value={form.name_en} onChange={upd('name_en')} />
        <Input placeholder="Name (DE)" value={form.name_de} onChange={upd('name_de')} />
      </div>
      <Textarea placeholder="Description (LT)" value={form.description} onChange={upd('description')} rows={3} data-testid="pf-desc" />
      <Textarea placeholder="Description (EN)" value={form.description_en} onChange={upd('description_en')} rows={2} />
      <Textarea placeholder="Description (DE)" value={form.description_de} onChange={upd('description_de')} rows={2} />
      <Input placeholder="Image URL" value={form.images[0] || ''} onChange={(e) => setForm({ ...form, images: [e.target.value] })} data-testid="pf-image" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger data-testid="pf-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="ongoing">Ongoing</SelectItem>
            <SelectItem value="upcoming">Upcoming</SelectItem>
          </SelectContent>
        </Select>
        <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
          <SelectTrigger data-testid="pf-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="commercial">Commercial</SelectItem>
            <SelectItem value="residential">Residential</SelectItem>
            <SelectItem value="development">Development</SelectItem>
            <SelectItem value="international">International</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-3 pt-2">
        <button onClick={() => onSave(form)} data-testid="pf-save" className="bg-gold text-black px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold">Save</button>
        <button onClick={onCancel} data-testid="pf-cancel" className="border border-white/20 text-white px-6 py-3 text-xs uppercase tracking-[0.2em]">Cancel</button>
      </div>
    </div>
  );
};

const NewsForm = ({ initial, onSave, onCancel }) => {
  const [form, setForm] = useState(initial);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  return (
    <div className="border border-gold/40 p-8 mb-8 bg-[#141414] space-y-4" data-testid="news-form">
      <Input placeholder="Title (LT)" value={form.title} onChange={upd('title')} data-testid="nf-title" />
      <Input placeholder="Title (EN)" value={form.title_en} onChange={upd('title_en')} />
      <Input placeholder="Title (DE)" value={form.title_de} onChange={upd('title_de')} />
      <Textarea placeholder="Body (LT)" value={form.body} onChange={upd('body')} rows={4} data-testid="nf-body" />
      <Textarea placeholder="Body (EN)" value={form.body_en} onChange={upd('body_en')} rows={3} />
      <Textarea placeholder="Body (DE)" value={form.body_de} onChange={upd('body_de')} rows={3} />
      <Input placeholder="Image URL" value={form.image} onChange={upd('image')} />
      <div className="flex gap-3 pt-2">
        <button onClick={() => onSave(form)} data-testid="nf-save" className="bg-gold text-black px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold">Save</button>
        <button onClick={onCancel} data-testid="nf-cancel" className="border border-white/20 text-white px-6 py-3 text-xs uppercase tracking-[0.2em]">Cancel</button>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user ? 'auth' : 'check');
  const [user, setUser] = useState(location.state?.user || null);
  const [projects, setProjects] = useState([]);
  const [news, setNews] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editingProject, setEditingProject] = useState(null);
  const [editingNews, setEditingNews] = useState(null);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) return;
    if (auth === 'auth') return;
    axios
      .get(`${API}/auth/me`, AXIOS_CFG)
      .then((r) => {
        if (!r.data.is_admin) {
          toast.error('Not authorized');
          setAuth('unauth');
          return;
        }
        setUser(r.data);
        setAuth('auth');
      })
      .catch(() => setAuth('unauth'));
  }, [auth]);

  const loadAll = () => {
    axios.get(`${API}/projects`).then((r) => setProjects(r.data || []));
    axios.get(`${API}/news`).then((r) => setNews(r.data || []));
    axios.get(`${API}/admin/messages`, AXIOS_CFG).then((r) => setMessages(r.data || [])).catch(() => {});
  };

  useEffect(() => { if (auth === 'auth') loadAll(); }, [auth]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, AXIOS_CFG).catch(() => {});
    setAuth('unauth');
    setUser(null);
    navigate('/admin', { replace: true });
  };

  const saveProject = async (form) => {
    try {
      if (form.id) await axios.put(`${API}/admin/projects/${form.id}`, form, AXIOS_CFG);
      else await axios.post(`${API}/admin/projects`, form, AXIOS_CFG);
      toast.success('Saved');
      setEditingProject(null);
      loadAll();
    } catch { toast.error('Save failed'); }
  };

  const deleteProject = async (id) => {
    if (!window.confirm('Delete project?')) return;
    try { await axios.delete(`${API}/admin/projects/${id}`, AXIOS_CFG); loadAll(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  const saveNews = async (form) => {
    try {
      if (form.id) await axios.put(`${API}/admin/news/${form.id}`, form, AXIOS_CFG);
      else await axios.post(`${API}/admin/news`, form, AXIOS_CFG);
      toast.success('Saved'); setEditingNews(null); loadAll();
    } catch { toast.error('Save failed'); }
  };

  const deleteNews = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API}/admin/news/${id}`, AXIOS_CFG); loadAll(); toast.success('Deleted'); }
    catch { toast.error('Delete failed'); }
  };

  if (auth === 'check') {
    return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-gold text-xs tracking-[0.3em] uppercase">Loading…</div>;
  }
  if (auth === 'unauth') return <AdminLogin />;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      <header className="border-b border-[#262626] sticky top-0 bg-[#0A0A0A]/95 backdrop-blur-xl z-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-6">
            <span className="text-sm text-white/60 hidden sm:block" data-testid="admin-user">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70 hover:text-gold">
              <LogOut size={14} /> {t('admin.logout')}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <h1 className="font-serif text-white text-4xl tracking-tighter mb-8">{t('admin.title')}</h1>

        <Tabs defaultValue="projects">
          <TabsList className="bg-transparent border-b border-[#262626] w-full justify-start rounded-none p-0 h-auto mb-8">
            <TabsTrigger value="projects" data-testid="tab-projects" className="data-[state=active]:text-gold data-[state=active]:border-gold border-b-2 border-transparent text-xs uppercase tracking-[0.2em] px-6 py-3 rounded-none">Projects</TabsTrigger>
            <TabsTrigger value="news" data-testid="tab-news" className="data-[state=active]:text-gold data-[state=active]:border-gold border-b-2 border-transparent text-xs uppercase tracking-[0.2em] px-6 py-3 rounded-none">News</TabsTrigger>
            <TabsTrigger value="messages" data-testid="tab-messages" className="data-[state=active]:text-gold data-[state=active]:border-gold border-b-2 border-transparent text-xs uppercase tracking-[0.2em] px-6 py-3 rounded-none">Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="projects">
            {editingProject ? (
              <ProjectForm initial={editingProject} onSave={saveProject} onCancel={() => setEditingProject(null)} />
            ) : (
              <button onClick={() => setEditingProject({ ...emptyProject })} data-testid="add-project" className="mb-8 inline-flex items-center gap-2 bg-gold text-black px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold">
                <Plus size={14} /> Add Project
              </button>
            )}
            <div className="space-y-px bg-[#262626]">
              {projects.map((p) => (
                <div key={p.id} className="bg-[#0A0A0A] p-6 flex items-center justify-between gap-4" data-testid={`admin-project-${p.id}`}>
                  <div className="flex items-center gap-4 min-w-0">
                    {p.images?.[0] && <img src={p.images[0]} alt="" className="w-16 h-16 object-cover"  loading="lazy" />}
                    <div className="min-w-0">
                      <div className="text-xs uppercase tracking-[0.2em] text-gold mb-1">{p.category} · {p.status}</div>
                      <div className="font-serif text-white text-lg truncate">{p.name}</div>
                      <div className="text-white/40 text-xs">{p.location}</div>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => setEditingProject({ ...p, images: p.images?.length ? p.images : [''] })} className="text-white/70 hover:text-gold p-2"><Edit2 size={14} /></button>
                    <button onClick={() => deleteProject(p.id)} className="text-white/70 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="news">
            {editingNews ? (
              <NewsForm initial={editingNews} onSave={saveNews} onCancel={() => setEditingNews(null)} />
            ) : (
              <button onClick={() => setEditingNews({ ...emptyNews })} data-testid="add-news" className="mb-8 inline-flex items-center gap-2 bg-gold text-black px-6 py-3 text-xs uppercase tracking-[0.2em] font-bold">
                <Plus size={14} /> Add News
              </button>
            )}
            <div className="space-y-px bg-[#262626]">
              {news.map((n) => (
                <div key={n.id} className="bg-[#0A0A0A] p-6 flex items-center justify-between gap-4">
                  <div className="font-serif text-white text-lg truncate">{n.title}</div>
                  <div className="flex gap-2">
                    <button onClick={() => setEditingNews(n)} className="text-white/70 hover:text-gold p-2"><Edit2 size={14} /></button>
                    <button onClick={() => deleteNews(n.id)} className="text-white/70 hover:text-red-400 p-2"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
              {news.length === 0 && <div className="bg-[#0A0A0A] p-6 text-white/40">No news yet.</div>}
            </div>
          </TabsContent>

          <TabsContent value="messages">
            <div className="space-y-px bg-[#262626]">
              {messages.map((m) => (
                <div key={m.id} className="bg-[#0A0A0A] p-6" data-testid={`message-${m.id}`}>
                  <div className="flex justify-between items-start gap-4 mb-2">
                    <div className="font-serif text-white text-lg">{m.name}</div>
                    <div className="text-xs text-white/40">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-sm text-gold mb-3">{m.email} · {m.phone}</div>
                  <p className="text-white/70 leading-relaxed whitespace-pre-wrap">{m.message}</p>
                </div>
              ))}
              {messages.length === 0 && <div className="bg-[#0A0A0A] p-6 text-white/40">No messages.</div>}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
